import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertCircle, 
  ChevronDown, 
  ChevronUp,
  Loader2,
  Ticket
} from 'lucide-react';
import Header from './components/Header';
import StatusCard from './components/StatusCard';
import PdfGuideCard from './components/PdfGuideCard';
import MethodsList from './components/MethodsList';
import { translations } from './i18n/translations';
import { detectLanguage, detectInitialLanguage } from './i18n/languageDetector';
import { analyzeMfaStatus, MFA_STATUS } from './services/mfaAnalyzer';
import { 
  getMsalInstance, 
  signIn, 
  signOut,
  getCurrentAccount,
  trySilentSignIn
} from './services/authService';
import { fetchUserProfile, fetchUserAuthMethods } from './services/graphService';
import { isMsalConfigured } from './config/authConfig';

export default function App() {
  // App state
  const [user, setUser] = useState(null);
  const [methods, setMethods] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Toggle for secondary details (accordion)
  const [showDetails, setShowDetails] = useState(false);

  // Localization: Auto-detect from Browser/OS language before login, fallback to 'en'
  const [currentLang, setCurrentLang] = useState(() => detectInitialLanguage());

  // Active translations
  const t = translations[currentLang] || translations.en;

  // Handler for manual language switcher
  const handleSelectLanguage = (langCode) => {
    setCurrentLang(langCode);
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem('mfa_user_language', langCode);
      }
    } catch (_) {}
  };

  /**
   * Fetch real data from Microsoft Graph
   */
  const loadRealData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const account = await getCurrentAccount();
      const profile = await fetchUserProfile();
      const rawMethods = await fetchUserAuthMethods();
      const analyzed = analyzeMfaStatus(rawMethods);

      setUser({
        ...(account || {}),
        ...(profile || {}),
        idTokenClaims: account?.idTokenClaims || {}
      });
      setMethods(analyzed.methods);
      setAnalysis(analyzed);

      // Detect language based on Entra ID usageLocation (primary) unless user explicitly selected one
      const savedLang = typeof window !== 'undefined' && window.sessionStorage?.getItem('mfa_user_language');
      if (!savedLang) {
        const detectedLang = detectLanguage(profile.usageLocation, profile.preferredLanguage);
        setCurrentLang(detectedLang);
      }
    } catch (err) {
      console.error("Failed to fetch data from Microsoft Graph:", err);
      setError(err.message || "Fehler beim Laden der MFA-Daten von Microsoft 365.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Initial App Setup: Check for active MSAL session, otherwise try a silent sign-in (SSO via
   * the Windows / Edge session) – the sign-in card only appears when that is not possible
   */
  useEffect(() => {
    async function init() {
      setIsLoading(true);
      if (isMsalConfigured()) {
        try {
          const instance = await getMsalInstance();
          if (instance) {
            const account = (await getCurrentAccount()) || (await trySilentSignIn());
            if (account) {
              await loadRealData();
              return;
            }
          }
        } catch (err) {
          console.warn("MSAL initialization failed:", err);
        }
      }

      setUser(null);
      setIsLoading(false);
    }

    init();
  }, [loadRealData]);

  // Handler for user login
  const handleSignIn = async () => {
    if (!isMsalConfigured()) {
      setError("Microsoft Entra ID ist in dieser Bereitstellung noch nicht konfiguriert (VITE_ENTRA_CLIENT_ID fehlt). Bitte trage deine Client-ID in die Umgebungsvariablen ein.");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await signIn();
    } catch (err) {
      console.error("Sign-in failed:", err);
      setError(err.message || "Anmeldung fehlgeschlagen.");
      setIsLoading(false);
    }
  };

  // Handler for user logout
  const handleSignOut = async () => {
    setIsLoading(true);
    await signOut();
    setUser(null);
    setAnalysis(null);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      
      {/* Sleek Minimal Header */}
      <Header
        user={user}
        translations={t}
        currentLang={currentLang}
        onSelectLanguage={handleSelectLanguage}
        onSignOut={handleSignOut}
      />

      {/* Main App Body */}
      <main className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12 flex flex-col justify-center">
        
        {/* Error notification banner */}
        {error && (
          <div className="mb-6 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-800 flex items-start gap-3 shadow-sm">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold">Hinweis</p>
              <p className="text-xs text-red-700 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* Signing in silently / loading the status */}
        {!user && isLoading ? (
          <div className="bg-white rounded-3xl border border-slate-200/90 p-8 sm:p-10 text-center shadow-xl shadow-slate-200/50">
            <img
              src="/eckes-granini-logo.jpg"
              alt="Eckes-Granini Group"
              className="h-14 sm:h-16 mx-auto object-contain mb-6"
            />
            <div className="flex items-center justify-center gap-3 text-sm font-semibold text-slate-600">
              <Loader2 className="w-5 h-5 text-[#008446] animate-spin" />
              <span>{t.checkingStatus}</span>
            </div>
          </div>
        ) : !user ? (
          /* Not Logged In State */
          <div className="bg-white rounded-3xl border border-slate-200/90 p-8 sm:p-10 text-center shadow-xl shadow-slate-200/50">
            <img 
              src="/eckes-granini-logo.jpg" 
              alt="Eckes-Granini Group" 
              className="h-14 sm:h-16 mx-auto object-contain mb-6"
            />
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight mb-2 font-jakarta">
              {t.appTitle}
            </h2>
            <p className="text-sm text-slate-600 mb-8 max-w-md mx-auto leading-relaxed">
              {t.loginPrompt}
            </p>
            <div className="max-w-sm mx-auto">
              <button
                type="button"
                onClick={handleSignIn}
                className="w-full py-4 px-6 rounded-2xl font-bold text-white bg-[#008446] hover:bg-[#00703b] shadow-lg shadow-[#008446]/25 transition-all transform hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-3 group"
              >
                {/* Official Microsoft 4-color Logo */}
                <svg className="w-5 h-5 shrink-0" viewBox="0 0 21 21" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                <span>{t.signInButton}</span>
              </button>
            </div>
          </div>
        ) : (
          /* Single Focused Card State */
          <div className="flex flex-col gap-6">
            
            {/* The Hero Card */}
            {analysis && (
              <StatusCard
                status={analysis.status}
                translations={t}
                isLoading={isLoading}
                onRefresh={loadRealData}
                hasFido={analysis.hasFido}
                hasAuthenticator={analysis.hasAuthenticator}
              />
            )}

            {/* Accompanying PDF Guide Card */}
            {analysis && analysis.status !== MFA_STATUS.AUTHENTICATOR && (
              <PdfGuideCard translations={t} language={currentLang} />
            )}

            {/* Registered Methods Accordion - ONLY rendered when status is OTHER_MFA (Neutral/SMS) */}
            {analysis && analysis.status === MFA_STATUS.OTHER_MFA && (
              <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm overflow-hidden transition-all">
                <button
                  type="button"
                  onClick={() => setShowDetails(!showDetails)}
                  className="w-full p-4 sm:p-5 flex items-center justify-between text-left hover:bg-slate-50/80 transition-colors"
                  aria-expanded={showDetails}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-800">
                      {t.detailsAccordionTitle}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                      {methods.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                    <span>{showDetails ? t.hideDetails : t.viewDetails}</span>
                    {showDetails ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </button>

                {showDetails && (
                  <div className="px-4 pb-5 pt-1 sm:px-6 border-t border-slate-100">
                    <MethodsList 
                      methods={methods} 
                      translations={t} 
                    />
                  </div>
                )}
              </div>
            )}

            {/* IT Self-Service Ticket Portal Link - ONLY shown when status is NOT optimal */}
            {analysis && analysis.status !== MFA_STATUS.AUTHENTICATOR && (
              <div className="text-center pt-2">
                <p className="text-xs text-slate-400 mb-2">
                  {t.supportTitle} {t.supportDesc}
                </p>
                <a
                  href={import.meta.env.VITE_TICKET_PORTAL_URL || "https://servicedesk.eckes-granini.com"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 shadow-2xs transition-colors"
                >
                  <Ticket className="w-4 h-4 text-[#008446]" />
                  <span>{t.supportButton} ↗</span>
                </a>
              </div>
            )}

          </div>
        )}

      </main>

      {/* Clean Minimal Centered Footer */}
      <footer className="py-6 text-center text-[11px] text-slate-400 border-t border-slate-200/60 bg-white/50">
        <div className="max-w-2xl mx-auto px-4 flex flex-col items-center justify-center gap-1.5 text-center">
          <span>&copy; {new Date().getFullYear()} Eckes-Granini Group GmbH</span>
        </div>
      </footer>

    </div>
  );
}
